import { reportSlugs } from '@app/ccf/company-report/slugs';
import {
  useFormattedSites,
  useGetPeriodOptions,
  useUnitOptions,
} from '@app/ccf/company-report/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetSites } from '@shared/api';
import { useGetOrganizationTimePeriods } from '@shared/api/hooks/ccf/periods';
import { useGetActivityTypeUnits } from '@shared/api/hooks/ccf/units';
import {
  useDeleteSoldFoodAndDrinkItemRecord,
  usePatchSoldFoodAndDrinkItemRecord,
} from '@shared/api/hooks/sold-goods/food-and-drink';
import {
  FoodOperationActivityRecord,
  InputModeEnum,
  PatchedFoodActivityRecordUpdate,
} from '@shared/api/types';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import SelectSearchFormField from '@shared/components/form/select-search-form-field';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { Skeleton } from '@shared/components/ui/skeleton';
import { useToast } from '@shared/components/ui/use-toast';
import { getObjectFromArrayWithKey } from '@shared/lib/utils';
import sentry from '@shared/services/sentry';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { object, string, z } from 'zod';
import { useSoldFoodAndDrinkItem } from '../../sold-food-and-drink-item-context';

interface SoldFoodAndDrinkItemSalesEditFormProps {
  foodActivityRecord?: FoodOperationActivityRecord;
  onOpenChange?: (open: boolean) => void;
  numberOfSales: number;
}

export const foodAndDrinkSalesSchema = object({
  uuid: string().min(1),
  quantity: z.coerce.number().min(0.001),
  timePeriod: string().min(1),
  unit: string().min(1),
  site: string().optional(),
  customer: string().optional(),
  totalMassOrVolume: z.coerce.number().min(0).optional(),
});

export default function SoldFoodAndDrinkItemSalesEditForm({
  foodActivityRecord,
  onOpenChange,
  numberOfSales,
}: SoldFoodAndDrinkItemSalesEditFormProps) {
  const { toast } = useToast();
  const { item } = useSoldFoodAndDrinkItem();
  const { itemUnit } = item;
  const activityInputMode =
    foodActivityRecord?.unit.symbol === 'ea'
      ? InputModeEnum.packs
      : InputModeEnum['total-weight'];

  const { mutateAsync: updateFoodRecord, isLoading: isUpdatingFoodRecord } =
    usePatchSoldFoodAndDrinkItemRecord();

  const { mutateAsync: deleteFoodRecord, isLoading: isDeletingFoodRecord } =
    useDeleteSoldFoodAndDrinkItemRecord();

  const {
    data: units,
    isLoading: unitsIsLoading,
    isError: unitsError,
  } = useGetActivityTypeUnits(reportSlugs.foodAndDrink);
  const { data: periods, isLoading: isPeriodsLoading } =
    useGetOrganizationTimePeriods();
  const { data: sites, isLoading: isSitesLoading } =
    useGetSites({
      page: 1,
      page_size: 1000,
    }) || [];

  const formattedSites = useFormattedSites(sites?.results || []);
  const getPeriodOptions = useGetPeriodOptions(periods!);
  const unitOptions = useUnitOptions(units!);

  const form = useForm<z.infer<typeof foodAndDrinkSalesSchema>>({
    resolver: zodResolver(foodAndDrinkSalesSchema),
  });

  const { getValues, setValue, watch, reset, formState } = form;

  const quantity = watch(`quantity`);
  const itemsPerPack = item.itemsPerPack;
  const itemWeight = item.itemMassOrVolume;

  const onSubmit = async (data: z.infer<typeof foodAndDrinkSalesSchema>) => {
    const updatedTotalWeightData = {
      timePeriod: data.timePeriod,
      quantity: data.quantity,
      unit: data.unit,
      uuid: data.uuid,
      site: data.site ? data.site : undefined,
    };

    const updatedPacksData = {
      timePeriod: data.timePeriod,
      quantity: data.quantity,
      uuid: data.uuid,
      site: data.site ? data.site : undefined,
    };

    const updateData: PatchedFoodActivityRecordUpdate =
      activityInputMode === InputModeEnum.packs
        ? updatedPacksData
        : updatedTotalWeightData;

    try {
      await updateFoodRecord({
        activityUuid: item.uuid,
        recordUuid: data.uuid,
        data: updateData,
      });

      onOpenChange?.(false);
      toast({
        title: 'Sale updated',
        variant: 'success',
      });
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to update sale',
        variant: 'destructive',
      });
    }
  };

  const onDelete = async () => {
    try {
      await deleteFoodRecord({
        activityUuid: item.uuid,
        recordUuid: foodActivityRecord!.uuid,
      });
      onOpenChange?.(false);
      toast({
        title: 'Sale deleted',
        variant: 'success',
      });
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to delete sale',
        variant: 'destructive',
      });
    }
  };

  const setTotalWeight = useMemo(() => {
    return (
      quantity: number,
      itemsPerPack: number,
      itemMassOrVolume: number
    ) => {
      setValue(`totalMassOrVolume`, quantity * itemsPerPack * itemMassOrVolume);
    };
  }, [setValue]);

  useEffect(() => {
    if (foodActivityRecord) {
      reset({
        timePeriod: foodActivityRecord.timePeriod.uuid,
        quantity: foodActivityRecord.quantity,
        unit: foodActivityRecord.unit.uuid,
        uuid: foodActivityRecord.uuid,
        totalMassOrVolume: foodActivityRecord.totalMassOrVolume as number,
        site: foodActivityRecord.site?.uuid || '',
        customer: foodActivityRecord.customer?.name || '',
      });
    }
  }, [foodActivityRecord, reset]);

  useEffect(() => {
    setTotalWeight(quantity, itemsPerPack, itemWeight);
  }, [quantity, itemsPerPack, setTotalWeight, itemUnit, itemWeight]);

  if (unitsIsLoading || isPeriodsLoading || isSitesLoading) {
    return (
      <div className="grid grid-cols-12 items-end gap-2">
        <Skeleton className="col-span-3 h-10 rounded-lg" />
        <Skeleton className="col-span-3 h-10 rounded-lg" />
        <Skeleton className="col-span-3 h-10 rounded-lg" />
        <Skeleton className="col-span-3 h-10 rounded-lg" />
      </div>
    );
  }

  if (!units) {
    throw new Error(
      `Failed to load units for ccf food activity rows ${unitsError || 'No data'}`
    );
  }

  if (!periods) {
    throw new Error(
      `Failed to load periods for food activity rows ${isPeriodsLoading || 'No data'}`
    );
  }

  const unit = getObjectFromArrayWithKey('uuid', getValues(`unit`), units);

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid grid-cols-12 items-start gap-2">
          <SelectFormField
            name="timePeriod"
            options={getPeriodOptions}
            disabled
            label="Period"
            className="col-span-3"
          />
          <SelectSearchFormField
            name="site"
            pluralName="sites"
            options={formattedSites}
            label="Site"
            disabled={isUpdatingFoodRecord}
            className="col-span-3"
          />
          <InputFormField
            name="customer"
            label="Customer"
            disabled
            className="col-span-3"
          />

          <InputFormField
            name="quantity"
            label={
              activityInputMode === InputModeEnum.packs
                ? 'Packs sold'
                : 'Total weight'
            }
            type="number"
            disabled={isUpdatingFoodRecord}
            className="col-span-3"
          />
          {activityInputMode === 'packs' && (
            <InputFormField
              name="totalMassOrVolume"
              label="Total weight"
              className="col-span-3"
              disabled
              adornmentRight={unit?.symbol}
            />
          )}

          {activityInputMode === 'total-weight' && (
            <SelectFormField
              name="unit"
              options={unitOptions}
              label="Unit"
              useShortLabel
              disabled={isUpdatingFoodRecord}
              className="col-span-3"
            />
          )}
        </div>
        <div className="flex justify-between">
          <Button
            type="button"
            variant="destructive"
            onClick={() => onDelete()}
            disabled={isUpdatingFoodRecord || numberOfSales <= 1}
            loading={isUpdatingFoodRecord || isDeletingFoodRecord}
          >
            Delete sale
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={isUpdatingFoodRecord}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isUpdatingFoodRecord}
              disabled={isUpdatingFoodRecord || !formState.isDirty}
            >
              {isUpdatingFoodRecord ? 'Updating...' : 'Update'}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
